/**
 * Products — the tracking-mode/brand/model layer above variants. `product.view` gates reads,
 * `product.create`/`product.edit`/`product.deactivate` gate the three kinds of write.
 *
 * There is no direct "delete" — only deactivation, matching the no-hard-deletes rule everywhere
 * else. Tracking-mode change is its own endpoint (not part of the general update) because the
 * server only allows it at zero stock; the dashboard hides the control rather than let it fail,
 * checking stock before rendering it, but the mutation still exists for when that's true.
 */

import { api } from '../api.js';
import { productListTag, productTag, stockListTag, variantListTag } from '../tags.js';
import type { CategoryId, Paged, Product, ProductId, ProductTrackingMode, ShopId, VariantId } from '../../types/api.js';

export type ListProductsParams = {
  readonly term?: string | undefined;
  readonly categoryId?: CategoryId | undefined;
  readonly trackingMode?: ProductTrackingMode | undefined;
  readonly includeInactive?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type CreateProductBody = {
  readonly categoryId: CategoryId;
  readonly name: string;
  readonly brand?: string | undefined;
  readonly baseModel?: string | undefined;
  readonly trackingMode: ProductTrackingMode;
  readonly serialLabel?: string | undefined;
  readonly description?: string | undefined;
};

/**
 * A product that is ready to sell, in one request — or nothing at all. The server creates the
 * product, its item, its prices and any opening stock in one transaction.
 */
export type CreateCompleteProductBody = {
  readonly categoryId: CategoryId;
  readonly name: string;
  readonly brand?: string | undefined;
  readonly baseModel?: string | undefined;
  readonly trackingMode: ProductTrackingMode;
  readonly serialLabel?: string | undefined;
  readonly attributes: Readonly<Record<string, string | null>>;
  readonly barcode?: string | undefined;
  readonly lowStockThreshold: number;
  readonly retailKes: number;
  /** The lowest a salesperson may sell at without a manager. Absent: the retail price itself. */
  readonly retailFloorKes?: number | undefined;
  readonly wholesaleKes?: number | undefined;
  readonly wholesaleMinQty?: number | undefined;
  readonly stockShopId?: ShopId | undefined;
  readonly stockQuantity?: number | undefined;
  readonly stockUnitCostKes?: number | undefined;
  readonly stockSerials: readonly string[];
};

export type CompleteProductCreated = {
  readonly productId: ProductId;
  readonly variantId: VariantId;
};

export type UpdateProductBody = {
  readonly categoryId: CategoryId;
  readonly name: string;
  readonly brand?: string | undefined;
  readonly baseModel?: string | undefined;
  readonly serialLabel?: string | undefined;
  readonly description?: string | undefined;
};

export const productsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listProducts: build.query<Paged<Product>, ListProductsParams>({
      query: (params) => ({ url: '/products', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: (result) => [
        productListTag(),
        ...(result?.items ?? []).map((product) => productTag(product.id)),
      ],
    }),

    getProduct: build.query<Product, ProductId>({
      query: (id) => `/products/${id}`,
      providesTags: (_result, _error, id) => [productTag(id)],
    }),

    createProduct: build.mutation<ProductId, CreateProductBody>({
      query: (body) => ({ url: '/products', method: 'POST', body }),
      invalidatesTags: [productListTag()],
    }),

    createCompleteProduct: build.mutation<CompleteProductCreated, CreateCompleteProductBody>({
      query: (body) => ({ url: '/products/complete', method: 'POST', body }),
      invalidatesTags: [productListTag(), variantListTag(), stockListTag()],
    }),

    updateProduct: build.mutation<void, { id: ProductId; body: UpdateProductBody }>({
      query: ({ id, body }) => ({ url: `/products/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [productTag(id), productListTag()],
    }),

    /** Legal only at zero stock across every shop — the server is the authority, this just calls it. */
    changeTrackingMode: build.mutation<
      void,
      { id: ProductId; trackingMode: ProductTrackingMode; serialLabel?: string | undefined }
    >({
      query: ({ id, ...body }) => ({ url: `/products/${id}/tracking-mode`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [productTag(id)],
    }),

    setProductActive: build.mutation<void, { id: ProductId; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/products/${id}/activation`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: (_result, _error, { id }) => [productTag(id), productListTag()],
    }),
  }),
});

export const {
  useListProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useCreateCompleteProductMutation,
  useUpdateProductMutation,
  useChangeTrackingModeMutation,
  useSetProductActiveMutation,
} = productsApi;
